import React, { useState } from "react";
import { OrganizationUserDto } from "../common/ObjectTypes";
import UserActionsModal from "../common/UserActionsModal";
import { BeatLoader } from "react-spinners";
import {
  getProjectUsers,
  reinviteProjectUser,
  removeUserFromProject,
  updateProjectUserRole,
} from "../../api/projectsApi";
import { toast } from "react-toastify";
import { ActionTypes, ProjectRoles } from "../common/Enums";
import UsersSearchBar from "../common/UserSearchBar";
import { useTranslation } from "react-i18next";
import ActiveUsersTable from "../common/ActiveUsersTable";
import PendingUsersTable from "../common/PendingUsersTable";
import { getSendOrRemoveInvitePermissionFromProjectRole } from "../common/helpers";
import { useProfile } from "../common/ProfileProvider";
import { useNavigate } from "react-router-dom";
import tailwindClassnames from "../common/TailwindClassnames";

type ProjectUsersTabProps = {
  selectedUser: OrganizationUserDto | undefined; // Allow undefined
  isOpen: boolean;
  onClose: () => void;
  activeUsers: OrganizationUserDto[];
  pendingUsers: OrganizationUserDto[];
  userSearchQuery: any;
  take: number;
  totalCount: number;
  skip: number;
  onPageClick: Function;
  onInviteUser: Function;
  onSelectUser: Function;
  loading: boolean;
  sendingInvite: boolean;
  roles: any[];
  projectId: number;
  projectName: string;
  perms: any[];
  loadUsersAndRoles: Function;
  onResendInvitation: Function;
  onRemoveInvitation: Function;
  onChangeSearch: Function;
  onSearch: Function;
  onChangeSelectRole: Function;
  handleRoleSelectEnter: Function;
};

const ProjectUsersTab: React.FC<ProjectUsersTabProps> = ({
  selectedUser,
  isOpen,
  onClose,
  activeUsers,
  pendingUsers,
  userSearchQuery,
  take,
  totalCount,
  skip,
  onPageClick,
  onInviteUser,
  onSelectUser,
  loading,
  roles,
  projectId,
  projectName,
  perms,
  loadUsersAndRoles,
  onResendInvitation,
  onRemoveInvitation,
  onChangeSearch,
  onSearch,
  onChangeSelectRole,
  sendingInvite,
  handleRoleSelectEnter,
}) => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();
  const { profile, fetchProfile } = useProfile();

  const onlyOneProjectOwner = async (projectId: number) => {
    const temp = await getProjectUsers({
      proj_id: projectId,
      all_users: true,
    });
    const activeUsers = temp.users.filter((user: any) => user.is_verified);
    if (
      activeUsers.filter((user: any) => user.role_id === ProjectRoles.OWNER)
        .length === 1
    ) {
      return true;
    }
    return false;
  };

  const handleActionSend = async (action_id: string, role_id: string) => {
    if (selectedUser === undefined) {
      return;
    }
    setIsSending(true);
    try {
      switch (action_id) {
        case ActionTypes.CHANGE_ROLE:
          if (
            selectedUser.role_id === ProjectRoles.OWNER &&
            (await onlyOneProjectOwner(projectId))
          ) {
            // make sure not to change the role of the last remaining owner
            toast.error(
              `${"User"} ${t("is the only owner of that project.")} ${t(
                "Please assign another owner before removing this user.",
              )}`,
            );
            break;
          }
          try {
            await updateProjectUserRole(projectId, selectedUser.user_id, {
              role_id: role_id,
            });
            toast.success(t("Role Updated"));
            // reload user perms if needed
            if (selectedUser.user_id === profile?.user_id) {
              await fetchProfile();
            }
          } catch (err) {
            console.error("Error updating role:", err);
            toast.error(t("Error Updating Role"));
          }
          break;

        case ActionTypes.RESEND_INVITATION:
          try {
            await reinviteProjectUser(projectId, selectedUser.user_id, []);
            toast.success(t("Invitation Resent"));
          } catch (err) {
            toast.error(t("Error Resending Invitation"));
          }
          break;

        case ActionTypes.REMOVE_USER:
          if (
            selectedUser.role_id === ProjectRoles.OWNER &&
            (await onlyOneProjectOwner(projectId))
          ) {
            // make sure not to remove the last remaining owner
            toast.error(
              `${"User"} ${t("is the only owner of that project.")} ${t(
                "Please assign another owner before removing this user.",
              )}`,
            );
            break;
          }
          try {
            await removeUserFromProject(projectId, selectedUser.user_id);
            toast.success(t("User Removed from Project"));
          } catch (err) {
            toast.error(t("Error Removing User from Project"));
          }

          if (selectedUser.user_id === profile?.user_id) {
            onClose();
            setIsSending(false);
            await fetchProfile(); // reload user perms if needed
            // redirect this user if they are removed from the project
            navigate("/");
            return; // return early to avoid calling loadUsersAndRoles()
          }
          break;

        default:
          console.error("Unknown action");
      }
    } finally {
      setIsSending(false);
      onClose();
      await loadUsersAndRoles();
    }
  };

  const projectRolesArray = Object.keys(ProjectRoles)
    .filter((key) => isNaN(Number(key))) // Filter out numeric keys
    .map((key) => ({
      name: key,
      value: ProjectRoles[key as keyof typeof ProjectRoles],
    }));

  return (
    <div className="">
      <UserActionsModal
        isOpen={isOpen}
        onClose={() => onClose()}
        selectedUser={selectedUser}
        roles={roles}
        scope="project"
        onSend={handleActionSend}
        perms={perms}
        entityName={projectName}
        isSending={isSending}
      />
      {/* mx-8 keeps tab content aligned with the tab strip above it.
          ActiveUsersTable and PendingUsersTable render their OWN tableCard
          surfaces (see their JSDoc) — no extra card wrappers here, matching
          OrganizationUsersTab. */}
      <div className="mx-8 mt-6 mb-4">
        <UsersSearchBar
          userSearchQuery={userSearchQuery}
          onInviteUser={onInviteUser}
          loading={loading}
          roles={projectRolesArray}
          onChangeSearch={onChangeSearch}
          onSearch={onSearch}
          onChangeSelectRole={onChangeSelectRole}
          handleRoleSelectEnter={handleRoleSelectEnter}
          sendingInvite={sendingInvite}
          hasInvitePermission={perms.some((perm: any) =>
            perm.name.includes("INVITE"),
          )}
        />
        {loading ? (
          <div className="sweet-loading flex justify-center w-full py-8">
            <BeatLoader
              color={tailwindClassnames.skyBlueDark}
              loading={true}
              size={15}
              margin={5}
            />
          </div>
        ) : (
          <ActiveUsersTable
            activeUsers={activeUsers}
            onSelectUser={onSelectUser}
            take={take}
            totalCount={totalCount}
            offset={skip}
            resultsLoaded={!loading}
            onPageClick={onPageClick}
          />
        )}
        {!loading && (
          <div className="mt-6">
            <PendingUsersTable
              pendingUsers={pendingUsers}
              onResendInvitation={onResendInvitation}
              onRemoveInvitation={onRemoveInvitation}
              getRemoveOrResendInvitePermissionFromUserRole={(role: number) =>
                getSendOrRemoveInvitePermissionFromProjectRole(role, perms)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectUsersTab;
